/**
 * idRangeRead — read a big SharePoint list in PARALLEL, by Id range.
 *
 * WHY (Mikko, 2026-10-01: BQC5 "too slow to load" next to BQC2 to BQC4). QC·BQC5
 * opens on `iSyn2_QcScans`, which the 2026-09-14 userform import took from ~23
 * rows to 36,030. SharePoint's pager hands back one page at a time and only
 * reveals the NEXT page's address inside the previous answer, so 36,030 rows is
 * eight requests that cannot overlap — eight round trips end to end, on every
 * cold open and every Refresh. BQC4 and the others read lists a fraction of
 * that size, which is most of why they open straight away.
 *
 * `Id` is indexed on every list, so `Id gt lo and Id le hi` is allowed past the
 * 5,000-item view threshold, and a range no wider than a page can never return
 * more than a page. Ids are unique, so the width is a hard ceiling on the rows
 * a range returns however many Ids have been deleted from it. That is what lets
 * the ranges be asked for AT ONCE: a few waves instead of a queue. It is the
 * same trick `ProductionGridService.listWipsForPayroll` already uses for WIPs;
 * this is the generic form of it.
 *
 * SAME ROWS, SAME ORDER as the sequential read. A sequential read comes back
 * by ascending Id; so does each range, and the ranges are joined in ascending
 * order. Callers break ties by list position (the QC ledger's "newest scan
 * wins a tie"), so order is part of the contract rather than a nicety — the
 * tests compare against the sequential result row for row.
 *
 * A row created WHILE the read is running (an Id above the one learned at the
 * start) is not in this read, exactly as it would not have been in a
 * sequential one that had already passed it; the next read has it.
 *
 * It does not decide what to do when this fails. A caller falls back to the
 * ordinary pager — slower, never wrong — and that fallback is also what tells
 * a missing-column error (which the sequential read reproduces, and the
 * column-generation chain in QcService depends on) from a problem with ranges.
 *
 * Pure: the list read is injected, so it is testable without SharePoint or
 * MSAL — see slipTemplate.ts on that trap.
 */
import { idRanges, mapPool } from "./wipIdRanges";

/** The slice of `SPQueryOptions` this needs. */
export interface IRangeReadQuery {
  select: string;
  filter?: string;
  orderby?: string;
  top?: number;
  singlePage?: boolean;
}

/** Pages per range: kept under SharePoint's 5,000-row page so one range is
 *  always exactly one request. */
export const RANGE_WIDTH = 4000;
/** How many ranges are in flight at once. Five, as the WIP read uses: enough to
 *  turn eight round trips into two waves, few enough not to invite throttling. */
export const RANGE_CONCURRENCY = 5;

export async function readByIdRanges<T>(
  getItems: (q: IRangeReadQuery) => Promise<T[]>,
  select: string,
  opts: {
    width?: number; concurrency?: number;
    /** The running row total as each range lands, so a caller that shows read
     *  progress (the QC importer does) keeps working. Ranges finish out of
     *  order, so this is a count, never a position. */
    onProgress?: (rowsSoFar: number) => void;
  } = {},
): Promise<T[]> {
  const width = opts.width ?? RANGE_WIDTH;
  const concurrency = opts.concurrency ?? RANGE_CONCURRENCY;

  const top = await getItems({ select: "Id", orderby: "Id desc", top: 1, singlePage: true });
  const maxId = Number((top[0] as { Id?: number } | undefined)?.Id ?? 0);
  if (!(maxId > 0)) return [];

  const ranges = idRanges(maxId, width);
  // A list that fits one range is one request either way; spare it the second.
  if (ranges.length === 1) {
    const rows = await getItems({ select, filter: `Id gt 0 and Id le ${maxId}`, orderby: "Id", top: 5000 });
    opts.onProgress?.(rows.length);
    return rows;
  }

  let rowsSoFar = 0;
  const pages = await mapPool(ranges, concurrency, async ({ lo, hi }) => {
    const rows = await getItems({ select, filter: `Id gt ${lo} and Id le ${hi}`, orderby: "Id", top: 5000 });
    rowsSoFar += rows.length;
    opts.onProgress?.(rowsSoFar);
    return rows;
  });
  return pages.flat();
}
